import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { usd, num } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { PageHeader } from "@/components/shell/PageHeader";
import { Panel, ProvenanceTag, StatusBadge, TableSkeleton, EmptyState } from "@/components/common/primitives";
import { Package, Search, Download, AlertTriangle } from "lucide-react";

const HEALTHS = [
  { k: "all", label: "All" },
  { k: "Healthy", label: "Healthy" },
  { k: "Low", label: "Low" },
  { k: "Critical", label: "Critical" },
  { k: "Excess", label: "Excess" },
];
const HEALTH_TONE = { Excess: "info", Healthy: "success", Low: "warning", Critical: "danger", Stockout: "danger" };
const WFS_TONE = { receiving: "info", in_transit: "warning", closed: "neutral" };
const segActive = "ez-seg-active";
const segIdle = "ez-seg text-ink-secondary";

export default function Inventory() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);
  const [health, setHealth] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => { api.inventory().then((r) => setRows(r.data)).catch(() => setErr(true)); }, []);

  const counts = useMemo(() => {
    const c = { all: rows?.length || 0 };
    (rows || []).forEach((r) => { c[r.health] = (c[r.health] || 0) + 1; });
    return c;
  }, [rows]);

  const summary = useMemo(() => {
    const list = rows || [];
    return {
      skus: list.length,
      units: list.reduce((s, r) => s + r.available, 0),
      value: list.reduce((s, r) => s + r.value, 0),
      atRisk: list.filter((r) => ["Low", "Critical"].includes(r.health)).length,
    };
  }, [rows]);

  const view = useMemo(() => {
    let v = [...(rows || [])];
    if (health !== "all") v = v.filter((r) => r.health === health);
    if (q.trim()) { const s = q.toLowerCase(); v = v.filter((r) => r.sku.toLowerCase().includes(s) || r.title.toLowerCase().includes(s)); }
    return v;
  }, [rows, health, q]);

  const th = "px-3 py-2 text-micro font-mono uppercase text-ink-muted font-medium whitespace-nowrap";

  if (err) return (
    <div className="space-y-4">
      <PageHeader title="Inventory" description="Operational stock view backed by an immutable movement ledger." meta={<ProvenanceTag kind="fixture" />} />
      <Panel className="p-12 text-center"><AlertTriangle className="h-7 w-7 text-red-600 mx-auto mb-2" /><div className="text-compact text-ink-primary">Couldn't load inventory.</div></Panel>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <PageHeader title="Inventory" description="Operational stock view backed by an immutable movement ledger — availability, days of supply and reorder needs."
        meta={<ProvenanceTag kind="fixture" />}
        actions={<button data-testid="inv-export" onClick={() => toast.success("Exported inventory (demo)")} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-ctrl border border-edge-subtle text-compact text-ink-secondary hover:bg-surface-2 ez-press"><Download className="h-4 w-4" />Export</button>} />

      {!rows ? <Panel className="p-4"><TableSkeleton rows={6} cols={7} /></Panel> : (
        <>
          <Panel className="px-1 py-1" testid="inv-ribbon">
            <div className="flex flex-wrap items-stretch divide-x divide-edge-subtle">
              {[
                ["Active SKUs", num(summary.skus), null],
                ["Units on hand", num(summary.units), null],
                ["Inventory value", usd(summary.value), "text-emerald-600"],
                ["Needs reorder", num(summary.atRisk), summary.atRisk ? "text-amber-600" : "text-ink-primary"],
              ].map(([label, val, clr]) => (
                <div key={label} className="px-4 py-2 min-w-[130px]">
                  <div className="text-micro uppercase tracking-wide text-ink-muted font-mono">{label}</div>
                  <div className={cn("text-metricsm font-mono tabnum mt-0.5", clr || "text-ink-primary")}>{val}</div>
                </div>
              ))}
            </div>
          </Panel>

          <div className="inline-flex items-center gap-1 p-1 rounded-module border border-edge-subtle max-w-full overflow-x-auto ez-scroll" role="tablist" aria-label="Stock health">
            {HEALTHS.map((h) => (
              <button key={h.k} data-testid={`inv-health-${h.k}`} aria-selected={health === h.k} onClick={() => setHealth(h.k)}
                className={cn("px-2.5 h-8 text-micro font-mono transition-colors", health === h.k ? segActive : segIdle)}>
                {h.label}<span className="ml-1.5 opacity-60">{counts[h.k] || 0}</span>
              </button>
            ))}
          </div>

          <Panel className="p-0 overflow-hidden" testid="inv-surface">
            <div className="flex items-center gap-2 p-3 border-b border-edge-subtle">
              <div className="flex items-center gap-1.5 h-8 px-2 rounded-ctrl border border-edge-subtle bg-surface-0">
                <Search className="h-3.5 w-3.5 text-ink-muted" />
                <input data-testid="inv-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU or product" className="bg-transparent text-compact focus:outline-none w-44" />
              </div>
              <span className="ml-auto text-micro font-mono text-ink-muted">{view.length} of {rows.length}</span>
            </div>

            {view.length === 0 ? (
              <EmptyState testid="inv-empty" icon={Package} title="No SKUs match your filters" hint="Try a different health status or search term." />
            ) : (
              <div className="overflow-x-auto ez-scroll">
                <table className="w-full text-compact">
                  <thead>
                    <tr className="bg-surface-2 text-left border-b border-edge-subtle">
                      <th className={cn(th, "sticky left-0 bg-surface-2")}>Product / SKU</th>
                      <th className={cn(th, "text-right")}>Available</th><th className={cn(th, "text-right")}>Inbound</th>
                      <th className={cn(th, "text-right")}>Reserved</th><th className={cn(th, "text-right")}>Unsellable</th>
                      <th className={cn(th, "text-right")}>30D units</th><th className={cn(th, "text-right")}>Days supply</th>
                      <th className={cn(th, "text-right")}>Reorder</th><th className={cn(th, "text-right")}>Avg cost</th>
                      <th className={cn(th, "text-right")}>Value</th><th className={th}>WFS</th><th className={th}>Health</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.map((r) => (
                      <tr key={r.sku} data-testid={`inv-row-${r.sku}`} className="border-b border-edge-subtle last:border-0 hover:bg-surface-1 h-12">
                        <td className="px-3 sticky left-0 bg-surface-0">
                          <div className="ident text-ink-primary flex items-center gap-2"><Package className="h-3.5 w-3.5 text-ink-muted shrink-0" />{r.sku}</div>
                          <div className="text-micro text-ink-muted truncate max-w-[180px]">{r.title}</div>
                        </td>
                        <td className="px-3 text-right font-mono tabnum">{num(r.available)}</td>
                        <td className="px-3 text-right font-mono tabnum text-ink-secondary">{num(r.inbound)}</td>
                        <td className="px-3 text-right font-mono tabnum text-ink-secondary">{num(r.reserved)}</td>
                        <td className={cn("px-3 text-right font-mono tabnum", r.unsellable > 0 ? "text-red-600" : "text-ink-muted")}>{num(r.unsellable)}</td>
                        <td className="px-3 text-right font-mono tabnum">{num(r.units_30d)}</td>
                        <td className={cn("px-3 text-right font-mono tabnum", r.days_supply <= 14 ? "text-red-600" : r.days_supply > 180 ? "text-signal-blue" : "text-ink-primary")}>{r.days_supply}</td>
                        <td className={cn("px-3 text-right font-mono tabnum", r.reorder > 0 ? "text-amber-600 font-semibold" : "text-ink-muted")}>{r.reorder > 0 ? `+${r.reorder}` : "—"}</td>
                        <td className="px-3 text-right font-mono tabnum text-ink-secondary">{usd(r.avg_cost)}</td>
                        <td className="px-3 text-right font-mono tabnum">{usd(r.value)}</td>
                        <td className="px-3"><StatusBadge tone={WFS_TONE[r.wfs]} className="capitalize">{r.wfs.replace(/_/g, " ")}</StatusBadge></td>
                        <td className="px-3"><StatusBadge tone={HEALTH_TONE[r.health]}>{r.health}</StatusBadge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-3 py-2 border-t border-edge-subtle text-micro font-mono text-ink-muted">Fixture ledger — figures are demo data, not live Walmart/WFS feeds.</div>
          </Panel>
        </>
      )}
    </div>
  );
}
